import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { Eye, EyeClosed } from '@phosphor-icons/react';

const LoginPage = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Bienvenido a Exploremos');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: '#F4ECE0' }}>
            <Toaster position="top-center" richColors />

            {/* Left: Brand panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#1F2D4B' }}>
                {/* Soft coral arc background motif */}
                <svg
                    viewBox="0 0 800 800"
                    className="absolute inset-0 w-full h-full opacity-15"
                    style={{ filter: 'blur(70px)' }}
                    preserveAspectRatio="xMidYMid slice"
                >
                    <path
                        d="M 100 600 Q 400 100 700 600"
                        fill="none"
                        stroke="#FB7185"
                        strokeWidth="200"
                        strokeLinecap="round"
                    />
                </svg>

                {/* Subtle dot grid */}
                <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                        backgroundSize: '32px 32px'
                    }}
                />

                <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
                    {/* Logo top */}
                    <svg viewBox="0 0 800 375" className="h-40 w-auto" aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
                        <path d="M 165 245 Q 400 110 635 245" fill="none" stroke="#FB7185" strokeWidth="14" strokeLinecap="round" />
                        <text x="400" y="340" textAnchor="middle" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="115" fontWeight="700" fill="#FFFFFF" letterSpacing="-4">exploremos</text>
                    </svg>

                    {/* Hero text */}
                    <div className="max-w-md">
                        <h1 className="font-display text-5xl xl:text-6xl text-white font-bold leading-[1.05] tracking-tight">
                            Viajar,<br />para volver a ti.
                        </h1>
                        <p className="text-[#B8C2D9] mt-6 text-base leading-relaxed">
                            Sistema de gestión de certificados de viaje.
                            Vende, emite y entrega en minutos.
                        </p>
                    </div>

                    <div className="text-xs text-white/40 font-medium">
                        © {new Date().getFullYear()} Exploremos · Todos los derechos reservados
                    </div>
                </div>
            </div>

            {/* Right: Login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-sm">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex justify-center mb-10">
                        <svg viewBox="0 0 800 375" className="h-40" aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
                            <path d="M 165 245 Q 400 110 635 245" fill="none" stroke="#FB7185" strokeWidth="14" strokeLinecap="round" />
                            <text x="400" y="340" textAnchor="middle" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="115" fontWeight="700" fill="#0F1B3D" letterSpacing="-4">exploremos</text>
                        </svg>
                    </div>

                    <div className="mb-8">
                        <h2 className="font-display text-3xl font-bold text-[#1F2D4B] tracking-tight">
                            Bienvenido a Exploremos
                        </h2>
                        <p className="text-sm text-[#4B5878] mt-2">
                            Inicia sesión en tu cuenta de POS
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-xs font-semibold text-[#1F2D4B]">
                                Correo electrónico
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="tu@exploremos.mx"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                className="h-11 rounded-md border-[#DCD1BC] bg-white focus-visible:ring-1 focus-visible:ring-[#1F2D4B] focus-visible:border-[#1F2D4B]"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="password" className="text-xs font-semibold text-[#1F2D4B]">
                                Contraseña
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                    className="h-11 rounded-md border-[#DCD1BC] bg-white focus-visible:ring-1 focus-visible:ring-[#1F2D4B] focus-visible:border-[#1F2D4B] pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B8474] hover:text-[#1F2D4B] transition-colors"
                                    data-testid="toggle-password-btn"
                                >
                                    {showPassword ? <EyeClosed size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full h-11 mt-2 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm font-semibold btn-lift"
                        >
                            {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
